import React from "react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Box,
  Button,
  Flex,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import NumberField from "../NumberField/NumberField";
import { InputFilter } from "./components";
import { AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";
import { showErrorToast, SORT_DIRECTIONS } from "../../utils";
import { utils, writeFile } from "xlsx";
import { useAuth } from "../../context";

/**
 * Currently this is near identical to DateTableV2. We will replace this with DataTableV2 soon.
 * @deprecated Use DataTableV2 instead
 */
function DataTable({
  data,
  columns,
  isLoading,
  serverSidePagination = false,
  queryPageCount,
  queryTotalCount,
  queryPageIndex = 0,
  setQueryPageIndex,
  queryPageSize = 20,
  setQueryPageSize,
  queryPageSortBy = [],
  setQueryPageSortBy,
  filterObj = {},
  setFilterObj,
  containerHeight,
  enableFilters = false,
  enableSorting = false,
  showPaginator = true,
  downloadButtonText = "Export",
  showDownloadButton = true,
  fontSize = "body.m",
  ...rest
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataTable",
  });
  const toast = useToast();

  const { user } = useAuth();

  const defaultColumn = React.useMemo(
    () => ({
      Filter: (data) => <InputFilter column={data.column} filterObj={filterObj} setFilterObj={setFilterObj} />,
    }),
    [filterObj, setFilterObj]
  );

  const table = useReactTable({
    data,
    columns,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    initialState: {
      pagination: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
    },
    pageCount: queryPageCount,

    manualSorting: serverSidePagination,
    manualFiltering: serverSidePagination,
    manualPagination: serverSidePagination,

    autoResetSortBy: false,
    autoResetFilters: false,

    enableFilters: enableFilters,
    enableSorting: enableSorting,
    defaultColumn: defaultColumn,
  });

  const headerBg = useColorModeValue("white", "gray.800");
  const headerShadowColor = useColorModeValue("gainsboro", "lightslategrey");
  const nonIntegerSymbols = ["e", "E", ",", ".", "-", "+"];

  /**
   * Exports TanStack Table data as JSON
   * @param {Object} table - The TanStack Table instance
   * @param {Object} options - Export options
   * @returns {Array} Array of JSON objects representing the table data
   */
  function exportTableAsJson(table, options = {}) {
    const { includePagination = false, processedValues = true, visibleColumnsOnly = true } = options;

    // Get the rows to export
    const rowsToExport = includePagination ? table.getRowModel().rows : table.getCoreRowModel().rows;

    // Get columns
    const columns = visibleColumnsOnly ? table.getVisibleLeafColumns() : table.getAllLeafColumns();

    // Transform the data
    const exportData = rowsToExport.map((row) => {
      const exportRow = {};

      columns.forEach((column) => {
        const columnId = column.id;
        const columnDef = column.columnDef;
        let value;

        try {
          // Attempt to use the cell function if it exists
          if (typeof columnDef.cell === "function") {
            value = columnDef.cell({ row, column, table });
            if (typeof value === "object" && value !== null && value.props) {
              value = value.props.children;
            } else if (typeof value === "object") {
              value = value.getValue();
            }
          } else {
            throw new Error("Cell function not available");
          }
        } catch {
          try {
            // Attempt to use TanStack's getValue method
            value = row.getValue(columnId);
          } catch {
            try {
              // Fall back to direct access via accessorKey
              if (columnDef.accessorKey) {
                value = row.original[columnDef.accessorKey];
              } else {
                throw new Error("Accessor key not available");
              }
            } catch {
              // Default to null if all methods fail
              value = null;
            }
          }
        }

        // Add to export row
        exportRow[columnId] = value;
      });

      return exportRow;
    });

    return exportData;
  }

  const downloadExcel = () => {
    const exportedData = exportTableAsJson(table);

    const ws = utils.json_to_sheet(exportedData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");
    writeFile(wb, "export.xlsx");
  };

  return (
    <Box>
      {showDownloadButton && (
        <Flex justifyContent="flex-end">
          <Button colorScheme="customScheme" m={2} onClick={() => downloadExcel()}>
            {downloadButtonText}
          </Button>
        </Flex>
      )}
      <Box
        pos="relative"
        borderWidth="thin"
        borderRadius="md"
        overflow="hidden"
        height={containerHeight}
        backgroundColor="white"
        {...rest}
      >
        {isLoading && (
          <Progress
            size="xs"
            isIndeterminate
            pos="absolute"
            insetInline="0"
            insetBlockStart="0"
            zIndex="3"
            colorScheme="customScheme"
          />
        )}
        <TableContainer height={`calc(100% - ${showPaginator ? "70px" : "0px"})`} overflowY="auto">
          <Table>
            <Thead>
              {table
                .getHeaderGroups()
                .map((headerGroup) =>
                  renderHeader(
                    headerGroup,
                    headerBg,
                    headerShadowColor,
                    setQueryPageSortBy,
                    filterObj,
                    setFilterObj,
                    serverSidePagination
                  )
                )}
            </Thead>
            <Tbody fontSize={fontSize}>
              {table.getFilteredRowModel().rows.length === 0 && (
                <Tr>
                  <Td colSpan={999} textAlign="center">
                    {t("noDataMessage")}
                  </Td>
                </Tr>
              )}
              {table.getRowModel().rows.map((row) => renderRow(row))}
            </Tbody>
          </Table>
        </TableContainer>
        {table.getFilteredRowModel().rows.length > 0 && showPaginator && (
          <Flex justifyContent="space-between" m={4} alignItems="center" overflowX="auto">
            <Flex>
              <Tooltip label={t("firstPageTooltip")}>
                <IconButton
                  aria-label="first-page-button"
                  onClick={() => {
                    table.setPageIndex(0);
                    if (setQueryPageIndex) setQueryPageIndex(0);
                    if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
                  }}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<AiOutlineDoubleLeft h={3} w={3} />}
                  mr={4}
                />
              </Tooltip>
              <Tooltip label={t("prevPageTooltip")}>
                <IconButton
                  aria-label="previous-page-button"
                  onClick={() => {
                    table.previousPage();
                    if (setQueryPageIndex) setQueryPageIndex(table.getState().pagination.pageIndex - 1);
                    if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
                  }}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<AiOutlineArrowLeft h={6} w={6} />}
                />
              </Tooltip>
            </Flex>

            <Flex alignItems="center">
              <Text flexShrink="0" mr={8}>
                {`${t("page")} `}
                <Text fontWeight="bold" as="span">
                  {table.getState().pagination.pageIndex + 1}
                </Text>
                {` ${t("of")} `}
                <Text fontWeight="bold" as="span">
                  {table.getPageCount()}
                </Text>
              </Text>
              <Text flexShrink="0">{`${t("goToPage")}: `}</Text>
              <NumberInput
                ml={2}
                mr={8}
                w={28}
                min={1}
                max={table.getPageCount()}
                clampValueOnBlur={false}
                onKeyDown={(event) => nonIntegerSymbols.includes(event.key) && event.preventDefault()}
                onChange={(value) => {
                  if (value !== "") {
                    if (value < 1 || value > table.getPageCount()) {
                      showErrorToast(toast, t("invalidPageMessage"));
                    } else {
                      toast.closeAll();
                      const page = value ? value - 1 : 0;
                      table.setPageIndex(page);
                      if (setQueryPageIndex) setQueryPageIndex(page);
                      if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
                    }
                  }
                }}
                defaultValue={table.getState().pagination.pageIndex + 1}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Select
                w={32}
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                  if (setQueryPageSize) setQueryPageSize(Number(e.target.value));
                }}
                title={`${t("show")} ${table.getState().pagination.pageSize}`}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {`${t("show")} ${pageSize}`}
                  </option>
                ))}
              </Select>
              <Text flexShrink="0" mr={8} ml={4}>
                {t("totalCount")}:{" "}
                <Text fontWeight="bold" as="span">
                  {serverSidePagination ? queryTotalCount : table.getFilteredRowModel().rows.length}
                </Text>
              </Text>
            </Flex>

            <Flex>
              <Tooltip label={t("nextPageTooltip")}>
                <IconButton
                  aria-label="next-page-button"
                  onClick={() => {
                    table.nextPage();
                    if (setQueryPageIndex) setQueryPageIndex(table.getState().pagination.pageIndex + 1);
                    if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
                  }}
                  isDisabled={!table.getCanNextPage()}
                  icon={<AiOutlineArrowRight h={6} w={6} />}
                />
              </Tooltip>
              <Tooltip label={t("lastPageTooltip")}>
                <IconButton
                  aria-label="last-page-button"
                  onClick={() => {
                    table.setPageIndex(table.getPageCount() - 1);
                    if (setQueryPageIndex) setQueryPageIndex(table.getPageCount() - 1);
                    if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
                  }}
                  isDisabled={!table.getCanNextPage()}
                  icon={<AiOutlineDoubleRight h={3} w={3} />}
                  ml={4}
                />
              </Tooltip>
            </Flex>
          </Flex>
        )}
      </Box>
    </Box>
  );
}

const renderRow = (row) => {
  const rowCustomStyles = row.original.rowCustomStyles ?? {};

  return (
    <Tr key={row.id} {...rowCustomStyles}>
      {row.getVisibleCells().map((cell) => {
        const isNumeric = !isNaN(cell.getValue());
        if (cell.getValue() === null || typeof cell.getValue() === "undefined") {
          cell.value = "";
        }

        let styleObj = {
          paddingInline: "var(--chakra-space-4)",
        };
        if (cell.column.getSize()) {
          styleObj["minWidth"] = cell.column.getSize();
          styleObj["maxWidth"] = cell.column.getSize();
        }
        if (typeof cell.column.columnDef.meta?.getStyleObj(cell?.getValue()) !== "undefined") {
          styleObj = {
            ...styleObj,
            ...cell.column.columnDef.meta?.getStyleObj(cell.getValue(), cell),
          };
        }

        return (
          <Td key={cell.id} whiteSpace="pre-wrap" style={styleObj}>
            {isNumeric && !cell?.column?.columnDef?.skipNumericCheck ? (
              <NumberField value={cell.getValue()} align={"left"} />
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </Td>
        );
      })}
    </Tr>
  );
};

const handleColumnHeaderClick = async (column, setQueryPageSortBy) => {
  let newSortDirection;
  switch (column.columnDef.sortDirection) {
    case SORT_DIRECTIONS.NONE:
      newSortDirection = SORT_DIRECTIONS.ASC;
      break;
    case SORT_DIRECTIONS.ASC:
      newSortDirection = SORT_DIRECTIONS.DESC;
      break;
    case SORT_DIRECTIONS.DESC:
      newSortDirection = SORT_DIRECTIONS.NONE;
      break;
    default:
      newSortDirection = SORT_DIRECTIONS.NONE;
  }

  if (setQueryPageSortBy) {
    setQueryPageSortBy([`${column.id},${newSortDirection}`]);
  }
};

function renderHeader(headerGroup, bg, shadowColor, setQueryPageSortBy, filterObj, setFilterObj, serverSideSorting) {
  return (
    <Tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        const column = header.column;
        const styleObj = {
          verticalAlign: "top",
          paddingInline: "var(--chakra-space-4)",
        };

        if (header.getSize()) {
          styleObj["minWidth"] = header.getSize();
          styleObj["maxWidth"] = header.getSize();
        }

        if (column.columnDef.customStyles && Object.keys(column.columnDef.customStyles)?.length > 0) {
          Object.keys(column.columnDef.customStyles).forEach((attr) => {
            styleObj[attr] = column.columnDef.customStyles[attr];
          });
        }

        return (
          <Th
            key={header.id}
            style={styleObj}
            pos="sticky"
            insetBlockStart="0"
            bg={bg}
            borderBlockEndWidth="thin"
            boxShadow={`inset 0 -1px 0 0 ${shadowColor}`}
            userSelect="none"
            // zIndex={1}
            cursor="default !important"
          >
            {column.getCanSort() &&
            (typeof column.columnDef.isSortable === "undefined" || column.columnDef.isSortable) ? (
              <Flex
                align="center"
                onClick={
                  serverSideSorting
                    ? () => handleColumnHeaderClick(column, setQueryPageSortBy)
                    : header.column.getToggleSortingHandler()
                }
                cursor="pointer"
              >
                <Box marginRight={1}>{flexRender(column.columnDef.header, header.getContext())}</Box>
                <Box>
                  {column.columnDef.sortDirection === SORT_DIRECTIONS.ASC || column.getIsSorted() === "asc" ? (
                    <FaSortUp />
                  ) : column.columnDef.sortDirection === SORT_DIRECTIONS.DESC || column.getIsSorted() === "desc" ? (
                    <FaSortDown />
                  ) : (
                    <FaSort />
                  )}
                </Box>
              </Flex>
            ) : (
              <Box>{flexRender(column.columnDef.header, header.getContext())}</Box>
            )}
            {column.getCanFilter()
              ? (column.columnDef?.filter?.component?.(column, column.columnDef?.filter?.params) ?? (
                  <InputFilter column={column} filterObj={filterObj} setFilterObj={setFilterObj} />
                ))
              : null}
          </Th>
        );
      })}
    </Tr>
  );
}

export default DataTable;
